import React, { useMemo, useState } from 'react';

const RANGE_MIN = 0;
const RANGE_MAX = 16;
const CALIBRATION_POINTS = [0, 4, 8, 12, 16];
const WORKSHEET_POINTS = [0, 3.2, 6.4, 9.6, 12.8, 16];

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function round(value, digits = 2) {
  const factor = 10 ** digits;
  return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
}

function formatBar(value) {
  return Number(value).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function polarPoint(cx, cy, radius, angle) {
  const radians = ((angle - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians),
  };
}

function simulatedReading(pressure, fullScaleError, direction, series = 1) {
  const spanRatio = clamp(Number(pressure) / RANGE_MAX, 0, 1);
  const hysteresis = direction === 'DESC' ? 0.025 * spanRatio : 0;
  const repeatability = series === 2 ? -0.012 * spanRatio : 0;
  return round(Number(pressure) + Number(fullScaleError) * spanRatio + hysteresis + repeatability, 3);
}

function buildSimulatedRows(fullScaleError) {
  return WORKSHEET_POINTS.map((setpoint, index) => ({
    point_no: index + 1,
    setpoint,
    unit: 'bar',
    s1_asc_pattern: setpoint,
    s1_asc_instrument: simulatedReading(setpoint, fullScaleError, 'ASC', 1),
    s1_desc_pattern: setpoint,
    s1_desc_instrument: simulatedReading(setpoint, fullScaleError, 'DESC', 1),
    s2_asc_pattern: setpoint,
    s2_asc_instrument: simulatedReading(setpoint, fullScaleError, 'ASC', 2),
    s2_desc_pattern: setpoint,
    s2_desc_instrument: simulatedReading(setpoint, fullScaleError, 'DESC', 2),
  }));
}

function PressureGauge({ value }) {
  const cx = 150;
  const cy = 150;
  const startAngle = -130;
  const endAngle = 130;
  const angle = startAngle + ((clamp(value, RANGE_MIN, RANGE_MAX) - RANGE_MIN) / (RANGE_MAX - RANGE_MIN)) * (endAngle - startAngle);
  const needleEnd = polarPoint(cx, cy, 91, angle);

  const ticks = Array.from({ length: 33 }, (_, index) => {
    const tickValue = index / 2;
    const tickAngle = startAngle + (index / 32) * (endAngle - startAngle);
    const major = index % 4 === 0;
    const outer = polarPoint(cx, cy, 116, tickAngle);
    const inner = polarPoint(cx, cy, major ? 98 : 106, tickAngle);
    const label = major ? polarPoint(cx, cy, 80, tickAngle) : null;
    return { index, tickValue, major, outer, inner, label };
  });

  return (
    <svg className="w-full max-w-[330px] h-auto" viewBox="0 0 300 300" role="img" aria-label={`Manómetro simulado indicando ${formatBar(value)} bar`}>
      <circle cx="150" cy="150" r="137" className="fill-slate-950 stroke-slate-500" strokeWidth="5" />
      <circle cx="150" cy="150" r="126" className="fill-slate-900 stroke-slate-700" strokeWidth="1" />

      {ticks.map((tick) => (
        <g key={tick.index}>
          <line
            x1={tick.inner.x}
            y1={tick.inner.y}
            x2={tick.outer.x}
            y2={tick.outer.y}
            className={tick.major ? 'stroke-slate-100' : 'stroke-slate-500'}
            strokeWidth={tick.major ? 2.5 : 1}
            strokeLinecap="round"
          />
          {tick.major ? (
            <text x={tick.label.x} y={tick.label.y + 4} textAnchor="middle" className="fill-slate-200 text-[12px]">
              {tick.tickValue}
            </text>
          ) : null}
        </g>
      ))}

      <text x="150" y="92" textAnchor="middle" className="fill-slate-400 text-[11px] tracking-widest">CALINS</text>
      <text x="150" y="207" textAnchor="middle" className="fill-slate-400 text-[11px]">bar</text>
      <text x="150" y="228" textAnchor="middle" className="fill-slate-500 text-[9px]">0 — 16</text>

      <line x1="150" y1="150" x2={needleEnd.x} y2={needleEnd.y} className="stroke-emerald-400" strokeWidth="5" strokeLinecap="round" />
      <line x1="150" y1="150" x2={polarPoint(cx, cy, 24, angle + 180).x} y2={polarPoint(cx, cy, 24, angle + 180).y} className="stroke-emerald-400" strokeWidth="6" strokeLinecap="round" />
      <circle cx="150" cy="150" r="13" className="fill-slate-200" />
      <circle cx="150" cy="150" r="5" className="fill-slate-700" />

      <rect x="106" y="244" width="88" height="31" rx="5" className="fill-slate-800 stroke-slate-600" />
      <text x="150" y="265" textAnchor="middle" className="fill-emerald-300 text-[16px] font-medium">{formatBar(value)}</text>
    </svg>
  );
}

export default function PressureCalibrationSimulator({ onLoadWorksheet }) {
  const [pressure, setPressure] = useState(8);
  const [fullScaleError, setFullScaleError] = useState(0.12);
  const [instrumentClass, setInstrumentClass] = useState(1.6);
  const [direction, setDirection] = useState('ASC');
  const [captured, setCaptured] = useState([]);
  const [worksheetLoaded, setWorksheetLoaded] = useState(false);

  const instrumentReading = useMemo(
    () => simulatedReading(pressure, fullScaleError, direction, 1),
    [pressure, fullScaleError, direction]
  );
  const error = round(instrumentReading - pressure, 3);
  const tolerance = round((instrumentClass / 100) * RANGE_MAX, 3);
  const isConforming = Math.abs(error) <= tolerance;

  const setAppliedPressure = (nextValue) => {
    const next = clamp(Number(nextValue), RANGE_MIN, RANGE_MAX);
    if (next > pressure) setDirection('ASC');
    if (next < pressure) setDirection('DESC');
    setPressure(next);
    setWorksheetLoaded(false);
  };

  const capturePoint = () => {
    const next = {
      id: `${direction}-${pressure}`,
      pressure: round(pressure, 2),
      direction,
      reading: instrumentReading,
      error,
      conforming: isConforming,
    };
    setCaptured((previous) => {
      const withoutSamePoint = previous.filter((item) => item.id !== next.id);
      return [...withoutSamePoint, next].sort((a, b) => a.pressure - b.pressure || a.direction.localeCompare(b.direction));
    });
  };

  const runAutomaticCycle = () => {
    const ascending = CALIBRATION_POINTS.map((setpoint) => ({
      id: `ASC-${setpoint}`,
      pressure: setpoint,
      direction: 'ASC',
      reading: simulatedReading(setpoint, fullScaleError, 'ASC', 1),
    }));
    const descending = [...CALIBRATION_POINTS].reverse().map((setpoint) => ({
      id: `DESC-${setpoint}`,
      pressure: setpoint,
      direction: 'DESC',
      reading: simulatedReading(setpoint, fullScaleError, 'DESC', 1),
    }));
    const completed = [...ascending, ...descending].map((item) => {
      const pointError = round(item.reading - item.pressure, 3);
      return {
        ...item,
        error: pointError,
        conforming: Math.abs(pointError) <= tolerance,
      };
    });
    setCaptured(completed);
    setPressure(0);
    setDirection('DESC');
    setWorksheetLoaded(false);
  };

  const loadWorksheet = () => {
    onLoadWorksheet?.(buildSimulatedRows(fullScaleError));
    setWorksheetLoaded(true);
  };

  return (
    <section className="rounded-xl border border-emerald-500/30 bg-slate-950/35 p-4 md:p-5 space-y-5" aria-labelledby="pressure-simulator-title">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="pressure-simulator-title" className="text-lg font-semibold text-slate-100">Simulación de calibración · Manómetro 0 a 16 bar</h2>
            <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-1 text-xs text-amber-200">Modo demostración</span>
          </div>
          <p className="mt-1 text-sm text-slate-300">Compará la presión del patrón con la indicación del instrumento y recorré los puntos en subida y bajada.</p>
        </div>
        <div className={`rounded-lg border px-3 py-2 text-sm font-medium ${isConforming ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200' : 'border-red-400/30 bg-red-400/10 text-red-200'}`} aria-live="polite">
          {isConforming ? 'Dentro de tolerancia' : 'Fuera de tolerancia'}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(270px,360px)_1fr] lg:items-center">
        <div className="flex justify-center">
          <PressureGauge value={instrumentReading} />
        </div>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-white/10 bg-white/5 p-3">
              <div className="text-xs text-slate-400">Patrón</div>
              <div className="mt-1 text-xl font-semibold text-slate-100 tabular-nums">{formatBar(pressure)} bar</div>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/5 p-3">
              <div className="text-xs text-slate-400">Instrumento</div>
              <div className="mt-1 text-xl font-semibold text-emerald-300 tabular-nums">{formatBar(instrumentReading)} bar</div>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/5 p-3">
              <div className="text-xs text-slate-400">Error</div>
              <div className={`mt-1 text-xl font-semibold tabular-nums ${isConforming ? 'text-slate-100' : 'text-red-300'}`}>{error >= 0 ? '+' : ''}{formatBar(error)} bar</div>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/5 p-3">
              <div className="text-xs text-slate-400">Tolerancia</div>
              <div className="mt-1 text-xl font-semibold text-slate-100 tabular-nums">±{formatBar(tolerance)} bar</div>
            </div>
          </div>

          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <label htmlFor="sim-pressure" className="text-sm font-medium text-slate-200">Presión aplicada</label>
              <output htmlFor="sim-pressure" className="text-sm tabular-nums text-slate-300">{formatBar(pressure)} bar · {direction === 'ASC' ? 'Ascendente' : 'Descendente'}</output>
            </div>
            <input id="sim-pressure" type="range" min="0" max="16" step="0.1" value={pressure} onChange={(event) => setAppliedPressure(event.target.value)} className="w-full accent-emerald-500" />
            <div className="mt-3 flex flex-wrap gap-2" aria-label="Puntos rápidos de calibración">
              {CALIBRATION_POINTS.map((point) => (
                <button key={point} type="button" onClick={() => setAppliedPressure(point)} className={`rounded border px-3 py-1.5 text-sm ${pressure === point ? 'border-emerald-400 bg-emerald-500/20 text-emerald-100' : 'border-white/15 text-slate-300 hover:bg-white/10'}`}>
                  {point} bar
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm text-slate-200">
              <span className="mb-1 flex justify-between gap-3"><span>Error a fondo de escala</span><span className="tabular-nums text-slate-400">{fullScaleError >= 0 ? '+' : ''}{formatBar(fullScaleError)} bar</span></span>
              <input type="range" min="-0.6" max="0.6" step="0.01" value={fullScaleError} onChange={(event) => { setFullScaleError(Number(event.target.value)); setWorksheetLoaded(false); }} className="w-full accent-blue-500" />
            </label>
            <label className="text-sm text-slate-200">
              <span className="mb-1 block">Clase del manómetro</span>
              <select value={instrumentClass} onChange={(event) => setInstrumentClass(Number(event.target.value))} className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-slate-900">
                {[0.6, 1, 1.6, 2.5, 4].map((value) => <option key={value} value={value}>Clase {String(value).replace('.', ',')} %</option>)}
              </select>
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={capturePoint} className="rounded bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500">Registrar punto actual</button>
            <button type="button" onClick={runAutomaticCycle} className="rounded border border-white/20 px-3 py-2 text-sm text-slate-100 hover:bg-white/10">Simular ciclo completo</button>
            <button type="button" onClick={() => setCaptured([])} className="rounded border border-white/15 px-3 py-2 text-sm text-slate-300 hover:bg-white/10">Limpiar simulación</button>
            <button type="button" onClick={loadWorksheet} className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500">Cargar ejemplo en la planilla</button>
          </div>
          {worksheetLoaded ? <p className="text-sm text-blue-200" role="status">Las dos series simuladas se cargaron abajo. Revisalas antes de guardar.</p> : null}
        </div>
      </div>

      {captured.length ? (
        <div className="overflow-x-auto rounded-lg border border-white/10">
          <table className="w-full min-w-[620px] text-sm">
            <thead className="bg-white/5 text-slate-300">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Recorrido</th>
                <th className="px-3 py-2 text-right font-medium">Patrón</th>
                <th className="px-3 py-2 text-right font-medium">Instrumento</th>
                <th className="px-3 py-2 text-right font-medium">Error</th>
                <th className="px-3 py-2 text-center font-medium">Resultado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {captured.map((point) => (
                <tr key={point.id}>
                  <td className="px-3 py-2 text-slate-200">{point.direction === 'ASC' ? 'Ascendente' : 'Descendente'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-200">{formatBar(point.pressure)} bar</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-200">{formatBar(point.reading)} bar</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-200">{point.error >= 0 ? '+' : ''}{formatBar(point.error)} bar</td>
                  <td className={`px-3 py-2 text-center font-medium ${Math.abs(point.error) <= tolerance ? 'text-emerald-300' : 'text-red-300'}`}>{Math.abs(point.error) <= tolerance ? 'Conforme' : 'No conforme'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <p className="text-xs text-slate-400">La simulación es didáctica. No guarda datos ni emite certificados por sí sola.</p>
    </section>
  );
}
